import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import DropZone from '@apps-common/upload-modal/i18nDropZone';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import {Box, Dialog, DialogContent, Stack, Typography} from '@mui/material';
import {DialogTitle} from '@apps-common/ui-components';
import {EXTENSIONS as SUPPORTED_EXTENSIONS} from '@apps-common/importer';

import {MODALS} from '@app/constants';
import {showModal} from '@app/actions/ui';
import {trackEvent} from '@app/analytics';
import {useDispatch} from 'react-redux';
import {useGetCollectionsCount} from '@app/api/collections';

import {
  MESSAGES_KEYS,
  getValue as getMessageSeen,
  setValue as setMessageSeen,
} from '@app/util/local-messages';
import {useAuth} from '@apps-common/auth-react';

const getExtension = path => path.toLowerCase().split('.').pop();

const WelcomeDialog = ({inFiles, setInFiles}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {user} = useAuth();
  const [open, setOpen] = useState(false);

  const wasShown = getMessageSeen(MESSAGES_KEYS.WELCOME_MESSAGE);

  const {data: totalCollections, isPending} = useGetCollectionsCount(
    user?.claims.organization_id,
    {
      enabled: !wasShown || !user,
    }
  );

  useEffect(() => {
    if (totalCollections === 0 && !wasShown) {
      setOpen(true);
    }
  }, [totalCollections, wasShown]);

  const onClose = () => {
    setMessageSeen(MESSAGES_KEYS.WELCOME_MESSAGE, true);
    setOpen(false);
  };

  if (wasShown || isPending) {
    return false;
  }

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          minHeight: '35vh',
        },
      }}
    >
      <DialogTitle title={t('welcomeMsg.title')} onClose={onClose} />
      <DialogContent sx={{display: 'flex', flexDirection: 'column'}}>
        <Stack spacing={2} flex={1}>
          <Typography variant="body1">{t('welcomeMsg.body1')}</Typography>
          <Box sx={{display: 'flex', alignItems: 'center'}}>
            <LightbulbOutlinedIcon fontSize="small" />
            <Typography variant="body1">{t('welcomeMsg.body2')}</Typography>
          </Box>
          <DropZone
            files={inFiles}
            onDrop={droppedFiles => {
              const dropped = droppedFiles.filter(
                file =>
                  SUPPORTED_EXTENSIONS.indexOf(getExtension(file.name)) >= 0 &&
                  !inFiles.find(f => f.name === file.name) //don't allow dupe files
              );
              trackEvent('Welcome Dialog', {
                action: 'Dropped Files',
                files: dropped,
              });
              onClose();
              dispatch(showModal(MODALS.UPLOAD));
              setInFiles(dropped);
            }}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

WelcomeDialog.propTypes = {
  inFiles: PropTypes.array,
  setInFiles: PropTypes.func,
};

export default WelcomeDialog;
